import { Singleton } from "DistributedTaskControls/Common/Factory";
import * as DTCResources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";
import * as ServiceEndpointContracts from "TFS/ServiceEndpoint/Contracts";
import { ServiceEndpointHttpClient } from "TFS/ServiceEndpoint/ServiceEndpointRestClient";
import * as VssContext from "VSS/Context";
import { getClient } from "VSS/Service";
import * as Utils_String from "VSS/Utils/String";
import * as Q from "q";

export class RepositoryPickerSource extends Singleton {

  public validateRepositoryName(value: string, connectionId: string, dataSource: string): IPromise<string> {
      if (Utils_String.ignoreCaseComparer(value, Utils_String.empty) !== 0) {
        // for reducing the number of API calls made
        if (this._promiseDictionary.hasOwnProperty(value)) {
          return this._promiseDictionary[value];
        }
        if (this._prevValue === value) {
          return Q.resolve(this._prevPromise);
        }

        this._prevValue = value;
        // this method is being called from onValueChanged and getErrorMessage of RepositoryPickerComponent with a delay of 100ms.
        // as the promise may not be resolved in 100ms, it may not yet be available in the dictionary.
        // So, we are saving the promise object before its resolved and re-using the same in the second call instead of making 2 API calls.
        this._prevPromise =  this._getRepositoryByName(value, connectionId, dataSource)
          .then((executeServiceEndpointRequestResult) => {
            if (executeServiceEndpointRequestResult !== null) {
              let errorMessage: string;
              if (Utils_String.equals(executeServiceEndpointRequestResult.statusCode, "ok", true) && executeServiceEndpointRequestResult.result.length > 0) {
                errorMessage = Utils_String.empty;
              } else {
                errorMessage = Utils_String.localeFormat(DTCResources.InvalidRepositoryName, value);
              }
              this._promiseDictionary[value] = Q.resolve(errorMessage);
              return this._promiseDictionary[value];
            }
          },    (error) => {
            return Q.reject(error);
          });

        return Q.resolve(this._prevPromise);
      } else {
        return Q.resolve(null);
      }
  }

  public getRepositories(connectionId: string, initialListEndpointRequestPromise: IPromise<ServiceEndpointContracts.ServiceEndpointRequest>): IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult> {
    const projectId = VssContext.getDefaultWebContext().project.id;
    let endpointRequestPromise = Q.defer<ServiceEndpointContracts.ServiceEndpointRequestResult>();

    initialListEndpointRequestPromise.then((initialListEndpointRequest: ServiceEndpointContracts.ServiceEndpointRequest) => {
      endpointRequestPromise.resolve(this._serviceEndpointHttpClient.executeServiceEndpointRequest(initialListEndpointRequest, projectId, connectionId));
    }, (error) => {
      endpointRequestPromise.reject(error);
    });

    return endpointRequestPromise.promise;
  }

  public searchRepositories(connectionId: string, searchEndpointRequest: ServiceEndpointContracts.ServiceEndpointRequest): IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult> {
    const projectId = VssContext.getDefaultWebContext().project.id;

    return this._serviceEndpointHttpClient.executeServiceEndpointRequest(searchEndpointRequest, projectId, connectionId);
  }

  public static instance(): RepositoryPickerSource {
    return super.getInstance<RepositoryPickerSource>(RepositoryPickerSource);
  }

  private _getRepositoryByName(value: string, connectionId: string, dataSource: string): IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult> {
    if (Utils_String.ignoreCaseComparer(value, Utils_String.empty) !== 0) {
      const projectId = VssContext.getDefaultWebContext().project.id;
      const dataSourceDetails: ServiceEndpointContracts.DataSourceDetails = {
        dataSourceName: dataSource,
        dataSourceUrl: null,
        resourceUrl: null,
        requestContent: null,
        requestVerb: "GET",
        parameters: { repositoryName: value },
        resultSelector: null,
        headers: null,
        initialContextTemplate: null
      };
      const resultTransformationDetails: ServiceEndpointContracts.ResultTransformationDetails = {
        resultTemplate: null,
        callbackContextTemplate: null,
        callbackRequiredTemplate: null
      };
      const serviceEndpointRequest: ServiceEndpointContracts.ServiceEndpointRequest = {
        serviceEndpointDetails: null,
        dataSourceDetails: dataSourceDetails,
        resultTransformationDetails: resultTransformationDetails
      };
      return this._serviceEndpointHttpClient.executeServiceEndpointRequest(
        serviceEndpointRequest,
        projectId,
        connectionId
      );
    }
  }

  private  _serviceEndpointHttpClient = getClient(ServiceEndpointHttpClient);
  private _prevValue: string;
  private _prevPromise: IPromise<string>;
  private _promiseDictionary: IDictionaryStringTo<IPromise<string>> = {};
}
